<script lang="ts">
  import { editor } from '../store/editor.svelte';
  import Icon, { type IconName } from './Icon.svelte';
  import type { Shape } from '../domain/types';

  const shapeIcon: Record<Shape['type'], IconName> = {
    arrow: 'arrow',
    rect: 'square',
    pencil: 'pencil',
    text: 'text',
    number: 'number',
    blur: 'blur',
  };

  function layerName(s: Shape): string {
    if (s.type === 'text') return `Текст: ${s.text.slice(0, 18) || '—'}`;
    if (s.type === 'number') return `Номер ${s.value}`;
    if (s.type === 'arrow') return 'Стрелка';
    if (s.type === 'rect') return 'Прямоугольник';
    if (s.type === 'pencil') return 'Карандаш';
    if (s.type === 'blur') return `Маска (${s.mode})`;
    return 'Слой';
  }

  // Сверху — верхние слои (как в Photoshop/Figma): реверс z-сортировки.
  const ordered = $derived([...editor.project.shapes].sort((a, b) => b.z - a.z));

  function handleLayerClick(id: string, e: MouseEvent | KeyboardEvent) {
    if (e.ctrlKey || e.metaKey) {
      editor.selectShape(id, true);
    } else if (e.shiftKey) {
      const startId = editor.selection[editor.selection.length - 1];
      if (!startId) {
        editor.selectShape(id, false);
        return;
      }
      const startIdx = ordered.findIndex(item => item.id === startId);
      const endIdx = ordered.findIndex(item => item.id === id);
      if (startIdx !== -1 && endIdx !== -1) {
        const minIdx = Math.min(startIdx, endIdx);
        const maxIdx = Math.max(startIdx, endIdx);
        editor.selection = ordered.slice(minIdx, maxIdx + 1).map(item => item.id);
      }
    } else {
      editor.selectShape(id, false);
    }
  }
</script>

<aside class="flex flex-col w-60 bg-panel border-r border-edge shrink-0 overflow-hidden">
  <div class="flex items-center justify-between px-3 py-2 border-b border-edge shrink-0">
    <span class="label normal-case text-ink font-semibold">
      {#if editor.selection.length > 1}
        Слои ({editor.selection.length})
      {:else}
        Слои
      {/if}
    </span>
    <div class="flex items-center gap-1">
      {#if editor.selection.length > 1}
        <button class="btn-icon !h-6 !w-6" title="Дублировать выбранные" onclick={() => editor.duplicateSelection()}>
          <Icon name="copy" size={13} />
        </button>
        <button class="btn-icon !h-6 !w-6" title="Скрыть/Показать" onclick={() => editor.toggleVisibleSelection()}>
          <Icon name="eye" size={13} />
        </button>
        <button class="btn-icon !h-6 !w-6" title="Заблокировать/Разблокировать" onclick={() => editor.toggleLockedSelection()}>
          <Icon name="lock" size={13} />
        </button>
        <button class="btn-icon !h-6 !w-6 text-red-500 hover:bg-red-500/10" title="Удалить выбранные" onclick={() => editor.deleteSelection()}>
          <Icon name="trash" size={13} />
        </button>
      {:else if editor.project.shapes.length > 0}
        <button
          class="btn-icon !h-6 !w-6 text-ink-dim hover:text-red-500 transition-colors"
          title="Очистить все слои"
          onclick={() => {
            if (confirm('Очистить все слои?')) {
              editor.clearAllShapes();
            }
          }}
        >
          <Icon name="trash" size={14} />
        </button>
      {/if}
    </div>
  </div>

  <div class="flex-1 overflow-y-auto">
    {#if ordered.length === 0}
      <div class="p-4 text-xs text-ink-dim text-center">Пока нет слоёв.<br />Выберите инструмент и нарисуйте что-нибудь.</div>
    {/if}

    {#each ordered as s (s.id)}
      <div
        class="group flex items-center gap-2 px-2 py-1.5 cursor-pointer border-b border-edge/40 hover:bg-panel-2 {editor.selection.includes(s.id) ? 'bg-[rgba(255,69,58,0.1)]' : ''}"
        onclick={(e) => handleLayerClick(s.id, e)}
        onkeydown={(e) => { if (e.key === 'Enter') handleLayerClick(s.id, e); }}
        role="button"
        tabindex="0"
      >
        <Icon name={shapeIcon[s.type]} size={14} />

        <span class="flex-1 text-xs truncate {s.visible ? '' : 'opacity-40 line-through'}">{layerName(s)}</span>

        <button class="opacity-0 group-hover:opacity-100 btn-icon !h-6 !w-6" title="Дублировать" onclick={(e) => { e.stopPropagation(); editor.duplicateShape(s.id); }}>
          <Icon name="copy" size={13} />
        </button>
        <button class="opacity-0 group-hover:opacity-100 btn-icon !h-6 !w-6" title="Удалить" onclick={(e) => { e.stopPropagation(); editor.deleteShape(s.id); }}>
          <Icon name="trash" size={13} />
        </button>
        <button class="btn-icon !h-6 !w-6" title={s.locked ? 'Разблокировать' : 'Заблокировать'} onclick={(e) => { e.stopPropagation(); editor.toggleLocked(s.id); }}>
          <Icon name={s.locked ? 'lock' : 'unlock'} size={13} />
        </button>
        <button class="btn-icon !h-6 !w-6" title={s.visible ? 'Скрыть' : 'Показать'} onclick={(e) => { e.stopPropagation(); editor.toggleVisible(s.id); }}>
          <Icon name={s.visible ? 'eye' : 'eyeOff'} size={13} />
        </button>
      </div>
    {/each}
  </div>

  {#if editor.selection.length === 1}
    <div class="flex items-center justify-center gap-1 p-1.5 border-t border-edge">
      <button class="btn-icon" title="На самый верх" onclick={() => editor.reorderLayer(editor.selection[0], 'top')}><Icon name="chevronUp" /><Icon name="chevronUp" /></button>
      <button class="btn-icon" title="Выше" onclick={() => editor.reorderLayer(editor.selection[0], 'up')}><Icon name="chevronUp" /></button>
      <button class="btn-icon" title="Ниже" onclick={() => editor.reorderLayer(editor.selection[0], 'down')}><Icon name="chevronDown" /></button>
      <button class="btn-icon" title="В самый низ" onclick={() => editor.reorderLayer(editor.selection[0], 'bottom')}><Icon name="chevronDown" /><Icon name="chevronDown" /></button>
    </div>
  {/if}
</aside>
